import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Archive, X } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, Overline, EASE } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-lined bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-teal focus:ring-2 focus:ring-teal/15";

function SubmissionsDrawer({ open, onClose, items, loading }) {
  const { t } = useLang();
  const d = t.contact.drawer;
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-slate-950/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            data-testid="submissions-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-bone shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-lined px-6 py-5">
              <h2 className="font-headline text-lg font-bold text-deep">{d.title}</h2>
              <button
                data-testid="submissions-drawer-close-btn"
                onClick={onClose}
                className="rounded-full border border-ink/15 p-2 text-ink transition-colors duration-300 hover:border-teal hover:text-teal"
                aria-label={d.close}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {loading ? (
                <p className="font-mono text-xs text-ink/50">{d.loading}</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-ink/55">{d.empty}</p>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => (
                    <li key={`${item.kind}-${item.id}`} className="rounded-2xl border border-lined bg-white p-5">
                      <div className="flex items-center justify-between">
                        <span
                          className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] ${
                            item.kind === "license" ? "bg-teal/10 text-teal" : "bg-ink/5 text-ink/60"
                          }`}
                        >
                          {item.kind === "license" ? d.licenseTag : d.contactTag}
                        </span>
                        <span className="font-mono text-[10px] text-ink/40">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-3 font-headline text-sm font-bold text-deep">{item.full_name}</p>
                      <p className="mt-0.5 text-xs text-ink/55">
                        {item.kind === "license" ? `${item.organization} · ${item.department}` : item.subject}
                      </p>
                      {item.message && (
                        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-ink/50">{item.message}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Contact() {
  const { t } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact-messages`, form);
      toast.success(f.success);
      setForm({ full_name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const openDrawer = async () => {
    setDrawerOpen(true);
    setLoadingItems(true);
    try {
      const [lic, con] = await Promise.all([
        axios.get(`${API}/license-requests`),
        axios.get(`${API}/contact-messages`),
      ]);
      const merged = [
        ...lic.data.map((x) => ({ ...x, kind: "license" })),
        ...con.data.map((x) => ({ ...x, kind: "contact" })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setItems(merged);
    } catch (err) {
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  return (
    <section className="bg-bone pb-24 pt-20 sm:pt-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Overline>{t.contact.hero.overline}</Overline>
            <h1 data-testid="contact-hero-title" className="mt-4 font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-deep sm:text-5xl">
              {t.contact.hero.title}
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink/65">{t.contact.hero.sub}</p>
            <button
              data-testid="view-submissions-drawer-btn"
              onClick={openDrawer}
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-teal/30 px-6 py-3 font-headline text-sm font-semibold text-teal transition-colors duration-300 hover:bg-teal hover:text-bone"
            >
              <Archive className="h-4 w-4" />
              {t.contact.drawer.btn}
            </button>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="card-glow rounded-3xl border border-lined bg-white p-7 sm:p-10"
            >
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">{f.name} *</label>
                  <input data-testid="contact-form-name-input" id="cf-name" required minLength={2} value={form.full_name} onChange={set("full_name")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">{f.email} *</label>
                  <input data-testid="contact-form-email-input" id="cf-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-subject" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">{f.subject} *</label>
                  <input data-testid="contact-form-subject-input" id="cf-subject" required minLength={2} value={form.subject} onChange={set("subject")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-message" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal">{f.message} *</label>
                  <textarea data-testid="contact-form-message-input" id="cf-message" required minLength={5} rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                </div>
                <button
                  data-testid="contact-form-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="rounded-full bg-teal px-8 py-4 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep disabled:opacity-60"
                >
                  {sending ? f.sending : f.submit}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>

      <SubmissionsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} items={items} loading={loadingItems} />
    </section>
  );
}
